/**
 * Permission hook.
 *
 * Two sources, and the split matters:
 *
 *   · CAPABILITIES come from the effective-permission document
 *     (GET /auth/permissions/me, held in permissionStore). Everything a user is
 *     allowed to *do* is in there.
 *   · SCOPE comes from the JWT. `isPlatformOperator` is authority over all
 *     tenants, and a permission profile is tenant-scoped — it structurally
 *     cannot express that, so it never will. See
 *     docs/RBAC_MIGRATION_PLAN.md §2.3.
 *
 * Nothing here derives from the role any more. `isAdmin` used to mean
 * `role === 'admin' || role === 'superadmin'`, which is exactly the
 * role-as-authorization the RBAC migration removes: it made the UI disagree
 * with the backend in both directions — hiding user management from an analyst
 * the API happily let through, and hiding it from an admin whose profile
 * granted the capability.
 *
 * This gates AFFORDANCES, not access. The backend is the control; the job here
 * is to not show a button that will 403, and to not hide one that will work.
 *
 * Usage:
 *   const { canAccess, hasModuleCapability, canResource } = usePermission();
 *   if (canAccess('connectors')) { ... }
 *   if (canResource('ontology', 'object_types', 'edit', objId)) { ... }
 */
import { useAuthStore } from '../store/authStore';
import { usePermissionStore } from '../store/permissionStore';
import {
  canResource as canResourceFn,
  hasModuleCapability as hasModuleCapabilityFn,
  moduleEnabled,
} from '../lib/permissionCatalog';

export function usePermission() {
  const user = useAuthStore((s) => s.user);
  const effective = usePermissionStore((s) => s.effective);
  const role = user?.role ?? 'viewer';
  const modules = user?.modules ?? [];  // legacy: empty = access to ALL

  // Module visibility: prefer the effective doc; fall back to legacy modules.
  const canAccess = (module: string): boolean => {
    if (effective) return moduleEnabled(effective, module);
    return modules.length === 0 || modules.includes(module);
  };

  // Granular helpers. They return FALSE while the document is still loading,
  // not true. They used to fail open, which was survivable while the role was
  // the real gate; now that these ARE the gate, failing open means the UI
  // offers every control for one render and then takes them away — and worse,
  // teaches users that a control exists when their profile says it does not.
  // `permissionsLoaded` is exposed so a caller can render a skeleton instead of
  // a wrongly-empty screen.
  const hasModuleCapability = (module: string, capability: string): boolean =>
    effective ? hasModuleCapabilityFn(effective, module, capability) : false;

  const canResource = (
    module: string, resourceType: string, capability: string, id?: string,
  ): boolean =>
    effective ? canResourceFn(effective, module, resourceType, capability, id) : false;

  return {
    /** Cross-tenant scope, from the JWT claim. Falls back to the role for
     *  tokens minted before the claim existed, matching
     *  AuthUser.is_platform_operator on the backend exactly. */
    isPlatformOperator: user?.is_platform_operator ?? (role === 'superadmin'),
    /** May reach the Admin console: module access, not a role. */
    canAdminister: canAccess('admin'),
    /** May manage users specifically — the narrower question UsersPage asks. */
    canManageUsers: hasModuleCapability('admin', 'manage_users'),
    canRead: true,
    canAccess,
    hasModuleCapability,
    canResource,
    /** False until GET /auth/permissions/me has answered. Render a loading
     *  state rather than a denied one while this is false. */
    permissionsLoaded: !!effective,
    effective,
    role,
    modules,
  };
}
